<template>
  <div v-if="!!phaseData">
    <radio-options
      class="mt-8"
      label="O paciente já tem diagnóstico de Dermatite atópica?"
      :options="hasDiagnosisOptions"
      v-model="phaseData.has_diagnosis.value"
      row
      clearable
      hide-details
      :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
      :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
    />
    <v-row v-if="phaseData.has_diagnosis.value" class="ml-6 mt-3">
      <v-col cols="12" class="pb-0">
        <v-col
          cols="12"
          class="pt-0 mt-3"
          v-if="!pharmaceuticalDomainConfig.SUMMARIZEDFLOW && pharmaceuticalDomainConfig.PHARMACEUTICAL_ID != 5"
        >
          <search-server-field
            :label="'Diagnóstico com: (Pediatra, Clínico, Dermatologista, Imunologista, Outro)'"
            path="specialty"
            :fetchAll="true"
            :item-text="
              (item) =>
                item.subspecialtyName ? `${item.specialtyName} - ${item.subspecialtyName}` : item.specialtyName
            "
            :required="phaseData.has_diagnosis.value"
            :multiple="false"
            v-model="phaseData.has_diagnosis.specialty"
          />
        </v-col>
        <field-label label="Feito no dia:">
          <v-col cols="3" class="pt-0 mt-3">
            <v-menu
              ref="pickDate"
              v-model="pickDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateFormatted"
                  label="Em:"
                  solo
                  prepend-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="diagnosisDate"
                no-title
                @change="pickDate = false"
                locale="pt-br"
                :max="$moment().toISOString()"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </field-label>
      </v-col>
    </v-row>
    <field-label label="Quando iniciaram os sintomas?" class="my-6">
      <v-row>
        <v-col cols="3">
          <v-menu
            ref="pickDate"
            v-model="pickDateStartSymptom"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="phaseData.inicio_sintomas ? $moment(phaseData.inicio_sintomas).format('MM/YYYY') : '00/0000'"
                solo
                v-bind="attrs"
                v-on="on"
                prepend-icon="mdi-calendar"
                hide-details
                class="mt-2 ml-3"
              />
            </template>
            <v-date-picker
              v-model="phaseData.inicio_sintomas"
              no-title
              type="month"
              locale="pt-br"
              @click:month="pickDateStartSymptom = false"
              :max="$moment().toISOString()"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
    </field-label>
    <field-label label="Quais sintomas o paciente apresenta?" class="mt-3" v-if="!attendance.data.summarizedFlow">
      <ul>
        <li style="color: #78909c">
          <h4 class="d-flex align-center blue-grey--text text--lighten-1 gap-2 topic mt-3">CRITÉRIOS MAIORES</h4>
        </li>
      </ul>
      <div v-for="symptomOption in symptomOptions.maiores" :key="symptomOption.key">
        <v-checkbox
          class="mt-0"
          :label="symptomOption.name"
          hide-details
          :value="symptomOption.key"
          v-model="phaseData.symptoms.maiores.selected"
          @change="resetAtopia(symptomOption.key)"
        />
        <div v-if="phaseData.symptoms.maiores.selected.includes('atopia')">
          <div v-for="(option, index) in symptomOption.options" :key="option.key">
            <v-checkbox
              v-model="phaseData.symptoms.maiores.atopia.value"
              hide-details
              :label="option.name"
              :value="option.key"
              class="mt-1 ml-3"
              @change="resetAtopiaSymptom(option.key)"
            />
            <div v-if="phaseData.symptoms.maiores.atopia.value.includes(option.key)">
              <div v-for="box in option.checkbox" :key="box.key">
                <v-checkbox
                  :label="box.name"
                  :value="box.key"
                  v-model="phaseData.symptoms.maiores.atopia.options[index].who"
                  class="ma-0 pa-0 ml-6 mb-1"
                  hide-details
                  @change="resetAtopiaDegree(option.key)"
                />
                <div v-if="phaseData.symptoms.maiores.atopia.options[index].who.includes('familiar')" class="d-flex">
                  <v-checkbox
                    v-for="grau in box.checkbox"
                    :key="grau.key"
                    :label="grau.name"
                    :value="grau.key"
                    v-model="phaseData.symptoms.maiores.atopia.options[index].degree"
                    class="ma-0 pa-0 ml-9"
                    hide-details
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ul>
        <li style="color: #78909c">
          <h4 class="d-flex align-center blue-grey--text text--lighten-1 gap-2 topic mt-6">CRITÉRIOS MENORES</h4>
        </li>
      </ul>
      <div v-for="symptomOption in symptomOptions.menores" :key="symptomOption.key">
        <field-label :label="symptomOption.label" class="mt-3">
          <div v-for="symptom in symptomOption.symptoms" :key="symptom.key">
            <v-checkbox
              class="mt-0"
              :label="symptom.name"
              :value="symptom.key"
              hide-details
              v-model="phaseData.symptoms.menores[symptom.category].selected"
            />
          </div>
        </field-label>
      </div>
    </field-label>

    <field-label class="mt-8" label="Anotações">
      <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" />
    </field-label>
    <reference-image-dialog ref="referenceImageDialog" />
  </div>
</template>

<script>
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import FieldLabel from '@/components/FieldLabel.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import SearchServerField from '@/components/SearchServerField.vue'
import TextArea from '@/components/TextArea.vue'
import ReferenceImageDialog from '../../ReferenceImageDialog.vue'

import {
  SYMPTOM_OPTIONS,
  SYMPTOM_OPTIONS_SUMMARIZED,
} from '@/components/AttendanceForm/atopicdermatitis/constants/symptom'

import { createNamespacedHelpers } from 'vuex'
const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')
const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')

export default {
  extends: BasePhase,
  components: { FieldLabel, ReferenceImageDialog, TextArea, RadioOptions, SearchServerField },
  computed: {
    ...attendanceGettersMap(['attendance']),
    ...authGettersMap(['pharmaceuticalDomainConfig']),
    hasDiagnosisOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
      ]
    },
    symptomOptions() {
      return this.attendance.data.summarizedFlow ? SYMPTOM_OPTIONS_SUMMARIZED : SYMPTOM_OPTIONS
    },
  },

  data() {
    return {
      dateFormatted: null,
      diagnosisDate: null,
      pickDate: false,
      pickDateStartSymptom: false,
    }
  },
  watch: {
    diagnosisDate(newVal) {
      this.phaseData.has_diagnosis.date = newVal
      this.dateFormatted = this.$moment(newVal).format('DD/MM/YYYY')
    },
  },
  methods: {
    getDefaultData() {
      const data = {
        has_diagnosis: {
          value: null,
          specialty: null,
          date: null,
        },
        inicio_sintomas: null,
        symptoms: {
          maiores: {
            selected: [],
            atopia: {
              value: [],
              options: [
                {
                  key: 'asma',
                  who: [],
                  degree: [],
                },
                {
                  key: 'rinite_alergica',
                  who: [],
                  degree: [],
                },
                {
                  key: 'dermatite_atopica',
                  who: [],
                  degree: [],
                },
              ],
            },
          },
          menores: {
            faciais: { selected: [] },
            triggers: { selected: [] },
            complicacoes: { selected: [] },
            outros: { selected: [] },
          },
        },
        notes: null,
      }
      return data
    },
    resetAtopia(atopia) {
      if (!this.phaseData.symptoms.maiores.selected.includes(atopia)) {
        this.phaseData.symptoms.maiores.atopia.value = []
        this.phaseData.symptoms.maiores.atopia.options.forEach((symptom) => {
          symptom.who = []
          symptom.degree = []
        })
      }
    },
    resetAtopiaSymptom(sintoma) {
      if (!this.phaseData.symptoms.maiores.atopia.value.includes(sintoma)) {
        this.phaseData.symptoms.maiores.atopia.options.forEach((option) => {
          if (option.key === sintoma) {
            option.who = []
            option.degree = []
          }
        })
      }
    },
    resetAtopiaDegree(sintoma) {
      this.phaseData.symptoms.maiores.atopia.options.forEach((option) => {
        if (option.key === sintoma && !option.who.includes('familiar')) {
          option.degree = []
        }
      })
    },
  },
  mounted() {
    this.phaseData.has_diagnosis.date
      ? (this.dateFormatted = this.$moment(this.phaseData.has_diagnosis.date).format('DD/MM/YYYY'))
      : null
  },
}
</script>

<style></style>
